import { Slider as MuiSlider, SliderProps } from "@mui/material";
import React from "react";
import { useDebounce } from "react-use";

type CustomMarkProps = { label: string; value: number; hoverText?: string };

type UseSliderReturn<TValue extends number | number[]> = {
  sliderValue: TValue;
  setSliderValue: React.Dispatch<React.SetStateAction<TValue>>;
};

export function useSlider<TValue extends number>(): UseSliderReturn<TValue>;
export function useSlider<TValue extends number[] | number>({
  initialValue,
}: {
  initialValue: TValue;
}): UseSliderReturn<TValue>;
export function useSlider<
  TValue extends number | number[] | undefined = number,
>({
  initialValue = 0,
}: {
  initialValue?: TValue;
} = {}) {
  const [sliderValue, setSliderValue] = React.useState(initialValue);

  return { sliderValue, setSliderValue };
}

export const CustomSlider = <TValue extends number | number[] = number>({
  sliderValue,
  setSliderValue,
  customMarks,
  marks,
  wprView,
  ...props
}: SliderProps & {
  setSliderValue: (value: TValue) => void;
  sliderValue: TValue;
  customMarks?: CustomMarkProps[];
  wprView?: boolean;
}) => {
  // TODO SLIDERVALUE TO MAX
  const { max } = props;
  const [localValue, setLocalValue] = React.useState(
    wprView && max ? max : sliderValue,
  );

  // eslint-disable-next-line consistent-return
  const getDate = React.useCallback(
    (value: number) => {
      if (!customMarks?.length) return value;
      return customMarks[sliderValue as number]?.hoverText ?? "";
    },
    [customMarks, sliderValue],
  );

  useDebounce(
    () => {
      setSliderValue(localValue as TValue);
    },
    100,
    [localValue],
  );

  return (
    <MuiSlider
      onChange={(_v, sVal) => setLocalValue(sVal as TValue)}
      step={null}
      value={localValue}
      valueLabelDisplay="auto"
      valueLabelFormat={getDate}
      marks={customMarks ?? marks}
      {...props}
    />
  );
};
